import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import {
    TrendingUp, ShoppingBag, Wallet, CheckCircle, Loader2, ArrowLeft,
    Clock, Award, Banknote, Calendar
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const GOLD = "#f4b339";
const GOLD_LIGHT = "#ffd166";
const STATUS_COLORS = {
    pending: "#f4b339",
    accepted: "#3ec2c2",
    preparing: "#ff9f1c",
    ready: "#00d4a4",
    completed: "#10b981",
    delivered: "#10b981",
    rejected: "#ff5470",
    cancelled: "#9ca3af",
};

export const PartnerAnalytics = () => {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(14);
    const [error, setError] = useState("");

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/login"); return; }
        if (user.role !== "store_owner" && user.role !== "admin") {
            navigate("/");
            return;
        }
        load(days);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading, days]);

    const load = async (n) => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(`/store/analytics?days=${n}`);
            setData(data);
        } catch (err) {
            setError(err.response?.data?.detail || "Σφάλμα φόρτωσης");
        } finally {
            setLoading(false);
        }
    };

    if (loading || !data) {
        return (
            <div className="min-h-screen cyber-bg grid place-items-center text-white">
                {error ? (
                    <div className="text-center">
                        <p className="text-sm text-[var(--gold-light)]">{error}</p>
                        <button onClick={() => navigate("/partner")} className="mt-4 px-4 h-10 rounded-full glass-gold text-xs">
                            Επιστροφή
                        </button>
                    </div>
                ) : (
                    <Loader2 className="animate-spin text-[var(--gold-light)]" size={36} />
                )}
            </div>
        );
    }

    const k = data.kpis || {};
    const daily = (data.daily || []).map((d) => ({
        ...d,
        label: d.day.slice(5), // MM-DD
    }));
    const peak = data.peak_hours || [];
    const top = data.top_items || [];
    const statusDist = Object.entries(data.status_distribution || {}).map(([name, value]) => ({
        name, value, color: STATUS_COLORS[name] || "#9ca3af",
    }));

    return (
        <div data-testid="partner-analytics" className="min-h-screen cyber-bg text-white pb-12">
            {/* HEADER */}
            <header className="sticky top-0 z-20 cyber-bg/95 backdrop-blur-xl border-b border-white/5 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate("/partner")}
                        data-testid="analytics-back"
                        className="h-10 w-10 grid place-items-center rounded-full glass-gold border border-[var(--gold)]/20"
                    >
                        <ArrowLeft size={16} className="text-[var(--gold-light)]" />
                    </button>
                    <div>
                        <p className="text-[9px] font-mono uppercase tracking-[0.32em] text-white/50">// partner_analytics</p>
                        <h1 className="font-heading text-2xl" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                            <span className="text-white">Στατιστικά</span>
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-1 glass-gold rounded-full p-1 border border-[var(--gold)]/20">
                    {[7, 14, 30].map((d) => (
                        <button
                            key={d}
                            data-testid={`days-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-3 h-8 rounded-full text-[10px] font-mono uppercase tracking-widest transition ${
                                days === d ? "bg-[var(--gold)] text-[#0B0B0F] font-bold" : "text-white/70 hover:text-white"
                            }`}
                        >
                            {d}μ
                        </button>
                    ))}
                </div>
            </header>

            <div className="px-5 py-5 max-w-6xl mx-auto space-y-6">
                {/* HERO KPIS */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KPI
                        title="Έσοδα"
                        value={`€${(k.revenue || 0).toFixed(2)}`}
                        sub={`Net €${(k.net_payout || 0).toFixed(2)}`}
                        icon={Wallet}
                        accent="from-[#f4b339] to-[#c98e15]"
                        testid="kpi-revenue"
                    />
                    <KPI
                        title="Παραγγελίες"
                        value={k.orders || 0}
                        sub={`AOV €${(k.avg_order || 0).toFixed(2)}`}
                        icon={ShoppingBag}
                        accent="from-emerald-400 to-emerald-600"
                        testid="kpi-orders"
                    />
                    <KPI
                        title="Σήμερα"
                        value={`€${(k.today_revenue || 0).toFixed(2)}`}
                        sub={`${k.today_orders || 0} παραγγελίες`}
                        icon={Calendar}
                        accent="from-fuchsia-400 to-rose-500"
                        testid="kpi-today"
                    />
                    <KPI
                        title="Acceptance"
                        value={`${(k.acceptance_rate ?? 100).toFixed(1)}%`}
                        sub="Ποσοστό αποδοχής"
                        icon={CheckCircle}
                        accent="from-cyan-400 to-emerald-400"
                        testid="kpi-acceptance"
                    />
                </section>

                {/* COMMISSION + PAYOUT BAR */}
                <section data-testid="payout-card" className="glass-gold rounded-3xl p-5 border border-[var(--gold)]/25 flex items-center gap-4">
                    <div className="h-14 w-14 grid place-items-center rounded-2xl bg-gradient-to-br from-[var(--gold)] to-[#c98e15]">
                        <Banknote size={26} strokeWidth={2} className="text-[#0B0B0F]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/50">// εκκρεμή πληρωμή</p>
                        <p className="font-heading text-3xl text-white" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                            €{(k.net_payout || 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-white/55">
                            μετά την προμήθεια <span className="text-[var(--gold-light)] font-bold">€{(k.commission_due || 0).toFixed(2)}</span> (10%)
                        </p>
                    </div>
                </section>

                {/* DAILY REVENUE TREND */}
                <section data-testid="chart-daily" className="glass-gold rounded-3xl p-5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-bold text-white flex items-center gap-2">
                            <TrendingUp size={14} className="text-[var(--gold-light)]" />
                            Έσοδα ανά ημέρα
                        </h2>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-white/40">{days} ημέρες</span>
                    </div>
                    <div className="h-56 w-full">
                        <ResponsiveContainer>
                            <AreaChart data={daily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={GOLD} stopOpacity={0.6} />
                                        <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ background: "rgba(11,11,15,0.95)", border: `1px solid ${GOLD}40`, borderRadius: 12, fontSize: 12 }}
                                    labelStyle={{ color: GOLD_LIGHT }}
                                    formatter={(v) => [`€${Number(v).toFixed(2)}`, "Έσοδα"]}
                                />
                                <Area type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} fill="url(#goldGrad)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                {/* TWO-COLUMN: orders trend + status pie */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div data-testid="chart-orders" className="glass-gold rounded-3xl p-5 border border-white/10 lg:col-span-2">
                        <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                            <ShoppingBag size={14} className="text-[var(--gold-light)]" />
                            Παραγγελίες ανά ημέρα
                        </h2>
                        <div className="h-48 w-full">
                            <ResponsiveContainer>
                                <BarChart data={daily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
                                    <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{ background: "rgba(11,11,15,0.95)", border: `1px solid ${GOLD}40`, borderRadius: 12, fontSize: 12 }}
                                        labelStyle={{ color: GOLD_LIGHT }}
                                    />
                                    <Bar dataKey="orders" fill={GOLD_LIGHT} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div data-testid="chart-status" className="glass-gold rounded-3xl p-5 border border-white/10">
                        <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                            <CheckCircle size={14} className="text-[var(--gold-light)]" />
                            Κατάσταση
                        </h2>
                        <div className="h-48 w-full">
                            {statusDist.length === 0 ? (
                                <div className="h-full grid place-items-center text-xs text-white/40">Καμία παραγγελία ακόμα</div>
                            ) : (
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={statusDist} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={64} strokeWidth={0}>
                                            {statusDist.map((s, i) => (<Cell key={i} fill={s.color} />))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ background: "rgba(11,11,15,0.95)", border: `1px solid ${GOLD}40`, borderRadius: 12, fontSize: 12 }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {statusDist.map((s) => (
                                <span key={s.name} className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-white/60">
                                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} /> {s.name} · {s.value}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PEAK HOURS HEATMAP */}
                <section data-testid="chart-peak" className="glass-gold rounded-3xl p-5 border border-white/10">
                    <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                        <Clock size={14} className="text-[var(--gold-light)]" />
                        Ώρες αιχμής (24h)
                    </h2>
                    <div className="h-40 w-full">
                        <ResponsiveContainer>
                            <LineChart data={peak} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="hour" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(h) => `${h}:00`} />
                                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ background: "rgba(11,11,15,0.95)", border: `1px solid ${GOLD}40`, borderRadius: 12, fontSize: 12 }}
                                    labelFormatter={(h) => `${h}:00`}
                                    labelStyle={{ color: GOLD_LIGHT }}
                                />
                                <Line type="monotone" dataKey="orders" stroke={GOLD} strokeWidth={2.5} dot={{ fill: GOLD, r: 3 }} activeDot={{ r: 6 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                {/* TOP ITEMS */}
                <section data-testid="top-items" className="glass-gold rounded-3xl p-5 border border-white/10">
                    <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                        <Award size={14} className="text-[var(--gold-light)]" />
                        Top προϊόντα
                    </h2>
                    {top.length === 0 ? (
                        <p className="text-xs text-white/40 text-center py-4">Καμία παραγγελία ακόμα.</p>
                    ) : (
                        <div className="space-y-2">
                            {top.map((it, i) => {
                                const max = Math.max(...top.map((x) => x.qty || 1));
                                const pct = ((it.qty || 0) / max) * 100;
                                return (
                                    <div key={i} className="flex items-center gap-3" data-testid={`top-item-${i}`}>
                                        <span className="w-6 text-[10px] font-mono text-white/40">#{i + 1}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm text-white truncate">{it.name}</span>
                                                <span className="text-[11px] font-mono text-[var(--gold-light)] shrink-0 ml-2">
                                                    {it.qty}× · €{it.revenue.toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-[var(--gold)] to-[var(--gold-light)] rounded-full transition-all" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

const KPI = ({ title, value, sub, icon: Icon, accent, testid }) => (
    <div data-testid={testid} className="glass-gold rounded-3xl p-4 border border-white/10 relative overflow-hidden">
        <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`} />
        <div className="flex items-center justify-between relative">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/50">{title}</p>
            <div className={`h-7 w-7 grid place-items-center rounded-lg bg-gradient-to-br ${accent}`}>
                <Icon size={13} strokeWidth={2.5} className="text-[#0B0B0F]" />
            </div>
        </div>
        <p className="font-heading text-2xl text-white mt-2" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{value}</p>
        <p className="text-[10px] text-white/50 mt-0.5">{sub}</p>
    </div>
);

export default PartnerAnalytics;
